import { CopyToClipboard } from '@/components/CopyToClipboard/CopyToClipboard';
import { EntityDetails } from '@/components/EntitySettings/EntityDetails';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { assert } from '@/utils/asserts';
import { Subtitle, Title } from '@tremor/react';
import s from './EntitySettingsInfo.module.scss';
import { ComponentProps } from 'react';
import { EntityType } from '@/types/Organization';
import { SubscriptionDetailsCard } from '@/components/Cards/SubscriptionDetailsCard/SubscriptionDetailsCard';
import { useCanCreateOrganizationStore } from '@/stores/canCreateOrganizationStore';

type BaseProps = {
  entityType: EntityType;
  entityId: string;
  entityName: string;
  showUserOptions?: boolean;
  children?: React.ReactNode;
  isAdmin: boolean;
};

type OrgProps = {
  isOrganization: true;
} & ComponentProps<typeof SubscriptionDetailsCard>;

type NonOrgProps = {
  isOrganization?: false | undefined;
} & {
  [K in keyof ComponentProps<typeof SubscriptionDetailsCard>]?: never;
};

type Props = BaseProps & (OrgProps | NonOrgProps);

export function EntitySettingsInfo({
  children,
  entityId,
  entityType,
  entityName,
  showUserOptions = false,
  isOrganization = false,
  isAdmin,
  ...rest
}: Props) {
  const workspaceId = useWorkspaceIdFromParams();
  const canUserCreateOrganizations = useCanCreateOrganizationStore(
    (state) => state.canCreateOrganization,
  );

  const entityNameTitle = entityType === 'workspaces' ? 'Workspace' : 'Organization';

  assert(workspaceId, 'workspaceId is required');
  return (
    <section className={s.pageContainer}>
      <Title className={s.pageTitle}>{entityNameTitle} Settings</Title>
      <CopyToClipboard copyText={entityId} text={`${entityNameTitle} ID: ${entityId}`} />
      {isOrganization && canUserCreateOrganizations ? (
        <SubscriptionDetailsCard
          {...(rest as ComponentProps<typeof SubscriptionDetailsCard>)}
        />
      ) : null}
      <EntityDetails name={entityName} key={entityName} entityType={entityType} />
      {showUserOptions && isAdmin ? (
        <>
          <hr style={{ width: '100%', marginTop: '2rem' }} />
          <section className={s.userOptionsContaienr}>
            <Subtitle className={s.userOptionsTitle}>User Options</Subtitle>
          </section>
          {children}
        </>
      ) : null}
    </section>
  );
}
